import TwitterBookmarkIcon from '@src/svgs/TwitterBookmarkIcon';
import TwitterLikeIcon from '@src/svgs/TwitterLikeIcon';
import TwitterReplyIcon from '@src/svgs/TwitterReplyIcon';
import TwitterRetweetIcon from '@src/svgs/TwitterRetweetIcon';
import TwitterShareIcon from '@src/svgs/TwitterShareIcon';
import TwitterVerifiedBadge from '@src/svgs/TwitterVerifiedBadge';
import TwitterViewIcon from '@src/svgs/TwitterViewIcon';
import React from 'react';
import styled from 'styled-components';

import { htmlToPlainText } from './htmlToPlainText';

/** Distribution channel slug this preview renders (PLAT-11038). */
export const CHANNEL_SLUG = 'x';

type TwitterViewport = 'desktop' | 'mobile';

type Props = {
  content: string;
  viewport?: TwitterViewport;
  authorName?: string;
  handle?: string;
  avatarUrl?: string;
};

const CHAR_LIMIT = 280;

const TwitterPreview: React.FC<Props> = ({
  content,
  viewport = 'desktop',
  authorName,
  handle,
  avatarUrl,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const plainText = htmlToPlainText(content);

  // Twitter truncates the displayed body at 280 chars, with a "Show more" affordance
  // when over-limit. Unlike LinkedIn, there is no visible-line cap.
  const isOverLimit = plainText.length > CHAR_LIMIT;
  const needsTruncation = isOverLimit;
  const overflowAmount = Math.max(0, plainText.length - CHAR_LIMIT);
  const truncatedText =
    needsTruncation && !isExpanded
      ? plainText.slice(0, CHAR_LIMIT).trimEnd()
      : plainText;

  const displayName = authorName || 'Your Name';
  const derivedHandle =
    handle ||
    (authorName
      ? '@' + authorName.toLowerCase().replace(/[^a-z0-9]+/g, '')
      : '@yourhandle');

  const isMobile = viewport === 'mobile';

  return (
    <Container>
      <PreviewCard viewport={viewport}>
        <PostBody isMobile={isMobile}>
          <Avatar>
            {avatarUrl ? (
              <AvatarImage src={avatarUrl} alt={displayName} />
            ) : (
              <AvatarPlaceholder />
            )}
          </Avatar>
          <PostColumn>
            <NameRow>
              <DisplayName>{displayName}</DisplayName>
              <VerifiedBadgeWrapper aria-label="Verified account">
                <TwitterVerifiedBadge
                  width={isMobile ? '14' : '16'}
                  height={isMobile ? '14' : '16'}
                />
              </VerifiedBadgeWrapper>
              <Handle>{derivedHandle}</Handle>
              <Dot>·</Dot>
              <PostTime>now</PostTime>
              <MoreButton aria-label="More">⋯</MoreButton>
            </NameRow>

            <PostContent>
              {truncatedText}
              {needsTruncation && !isExpanded && (
                <ShowToggle onClick={() => setIsExpanded(true)}>
                  {' '}
                  Show more
                </ShowToggle>
              )}
              {needsTruncation && isExpanded && (
                <ShowToggle onClick={() => setIsExpanded(false)}>
                  {' '}
                  Show less
                </ShowToggle>
              )}
            </PostContent>

            <PostActions>
              <ActionButton variant="reply" aria-label="Reply">
                <TwitterReplyIcon />
                <ActionCount>3</ActionCount>
              </ActionButton>
              <ActionButton variant="retweet" aria-label="Retweet">
                <TwitterRetweetIcon />
                <ActionCount>2</ActionCount>
              </ActionButton>
              <ActionButton variant="like" aria-label="Like">
                <TwitterLikeIcon />
                <ActionCount>6</ActionCount>
              </ActionButton>
              <ActionButton variant="view" aria-label="View">
                <TwitterViewIcon />
                <ActionCount>2.4K</ActionCount>
              </ActionButton>
              <UtilityGroup>
                <ActionButton variant="bookmark" aria-label="Bookmark">
                  <TwitterBookmarkIcon />
                </ActionButton>
                <ActionButton variant="share" aria-label="Share">
                  <TwitterShareIcon />
                </ActionButton>
              </UtilityGroup>
            </PostActions>
          </PostColumn>
        </PostBody>
      </PreviewCard>

      <CharacterCount>
        {plainText.length.toLocaleString()} / {CHAR_LIMIT} characters
        {isOverLimit && <> · {overflowAmount} over</>}
      </CharacterCount>
    </Container>
  );
};

export default TwitterPreview;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PreviewCard = styled.div<{ viewport: 'desktop' | 'mobile' }>`
  background: white;
  border: 1px solid #eff3f4;
  border-radius: ${props => (props.viewport === 'mobile' ? '0' : '16px')};
  overflow: hidden;
  width: 100%;
  max-width: ${props => (props.viewport === 'mobile' ? '375px' : '598px')};
  margin: 0 auto;
  box-shadow: ${props =>
    props.viewport === 'mobile' ? '0 0 0 1px #eff3f4' : 'none'};
`;

const PostBody = styled.div<{ isMobile: boolean }>`
  display: flex;
  gap: ${props => (props.isMobile ? '10px' : '12px')};
  padding: ${props => (props.isMobile ? '12px' : '12px 16px')};
`;

const PostColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
`;

const AvatarPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #1d9bf0 0%, #0c7abf 100%);
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const VerifiedBadgeWrapper = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
`;

const DisplayName = styled.span`
  color: #0f1419;
  font-size: 15px;
  font-weight: 700;
`;

const Handle = styled.span`
  color: #536471;
  font-size: 15px;
`;

const Dot = styled.span`
  color: #536471;
  font-size: 15px;
`;

const PostTime = styled.span`
  color: #536471;
  font-size: 15px;
`;

const MoreButton = styled.button`
  margin-left: auto;
  align-self: flex-start;
  background: transparent;
  border: none;
  color: #536471;
  font-size: 14px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
`;

const PostContent = styled.div`
  padding: 2px 0 12px;
  color: #0f1419;
  font-size: 15px;
  line-height: 1.3125;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const ShowToggle = styled.span`
  && {
    color: #1d9bf0;
    font-weight: 400;
    cursor: pointer;
  }

  &:hover {
    text-decoration: underline;
  }
`;

const PostActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0 0;
`;

const UtilityGroup = styled.div`
  display: flex;
  gap: 4px;
`;

type ActionVariant =
  | 'reply'
  | 'retweet'
  | 'like'
  | 'view'
  | 'bookmark'
  | 'share';

const ACTION_PALETTE: Record<ActionVariant, { bg: string; fg: string }> = {
  reply: { bg: 'rgba(29, 155, 240, 0.1)', fg: '#1d9bf0' },
  retweet: { bg: 'rgba(0, 186, 124, 0.1)', fg: '#00ba7c' },
  like: { bg: 'rgba(249, 24, 128, 0.1)', fg: '#f91880' },
  view: { bg: 'rgba(29, 155, 240, 0.1)', fg: '#1d9bf0' },
  bookmark: { bg: 'rgba(29, 155, 240, 0.1)', fg: '#1d9bf0' },
  share: { bg: 'rgba(29, 155, 240, 0.1)', fg: '#1d9bf0' },
};

const ActionButton = styled.button<{ variant: ActionVariant }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 6px;
  border: none;
  background: transparent;
  color: #536471;
  font-size: 13px;
  font-weight: 400;
  cursor: pointer;
  border-radius: 9999px;
  transition:
    background 0.15s,
    color 0.15s;

  & > svg {
    display: block;
    flex-shrink: 0;
  }

  &:hover {
    background: ${props => ACTION_PALETTE[props.variant].bg};
    color: ${props => ACTION_PALETTE[props.variant].fg};
  }
`;

const ActionCount = styled.span`
  line-height: 1;
`;

const CharacterCount = styled.div`
  text-align: right;
  font-size: 11px;
  color: var(--text-muted);
  padding: 0 4px;
`;
